import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Modal from "../../../../../components/Modal";
import Input from "../../../../../components/Input";
import RadioButtonsGroup from "../../../../../components/RadioButton";
import messages from "../../../../../assets/locale/messages";
import {
  EnglishAndSpecialCharacters,
  ArabicAndSpecialCharacters,
  ATLEAST_TWO_ENGLISH_CHARACTERS,
  ATLEAST_TWO_ARABIC_CHARACTERS,
  NUMBERS_REGEX,
} from "../../../../../utils/Patterns";

import { addServiceRequest } from "../../../../../store/Services/actions";

const AddEditManualServiceModal = ({
  open,
  setOpen,
  selectedService,
  setSelectedService,
  choosenSpecialtyId,
  onSuccess,
  allowMultiSessions = false,
  serviceProviderId,
}) => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const { branches, shared } = messages[lang];

  const formik = useFormik({
    initialValues: {
      serviceNameEN: selectedService?.name_en || "",
      serviceNameAR: selectedService?.name_ar || "",
      serviceType: selectedService?.service_type || "single_service",
    },
    validationSchema: Yup.object({
      serviceNameEN: Yup.string()
        .required("required")
        .matches(EnglishAndSpecialCharacters, "englishAndSpecialCharacters")
        .matches(ATLEAST_TWO_ENGLISH_CHARACTERS, "atleastTwoEnglishCharacters"),
      serviceNameAR: Yup.string()
        .required("required")
        .matches(ArabicAndSpecialCharacters, "arabicAndSpecialCharacters")
        .matches(ATLEAST_TWO_ARABIC_CHARACTERS, "atleastTwoArabicCharacters"),
    }),
    onSubmit: async ({
      serviceNameEN,
      serviceNameAR,
      serviceType,
    }) => {
      const data = {
        title_en: serviceNameEN.trim(),
        title_ar: serviceNameAR.trim(),
        specialty_id: choosenSpecialtyId,
        service_type: serviceType || "single_service",
        created_for_service_provider_id: serviceProviderId,
      };

      if (!selectedService?.serviceId) {
        dispatch(
          addServiceRequest(data, () => {
            onSuccess();
            handleCancelOrClose();
          })
        );
      }
    },
  });

  // Destructure formik for easier access to form methods
  const {
    values,
    handleSubmit,
    setFieldTouched,
    setFieldValue,
    resetForm,
    touched,
    errors,
    dirty,
    isValid,
    validateField,
  } = formik;

  useEffect(() => {
    setFieldValue("serviceNameEN", selectedService?.name_en || "");
    setFieldValue("serviceNameAR", selectedService?.name_ar || "");
    setFieldValue("serviceType", selectedService?.service_type || "single_service");
   
  }, [selectedService, setFieldValue]);

  const handleCancelOrClose = () => {
    // Reset form to initial values
    resetForm({
      values: {
        serviceNameEN: "",
        serviceNameAR: "",
        serviceType: "single_service",
      },
    });
    setSelectedService(null);
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      handleClose={handleCancelOrClose}
      title={
        <span className="fsize-26 fweight-400">
          {
            branches.addBranch.specialtiesAndServices[
              selectedService
                ? "editService"
                : allowMultiSessions
                ? "createNewServiceSessions"
                : "createNewService"
            ]
          }
        </span>
      }
      contentClass="mb-3 pb-2 text-primary"
      content={
        <form onSubmit={handleSubmit}>
          {allowMultiSessions && (
            <div className="mb-4">
              <div className="text-dark font-medium mb-2">
                {branches.addBranch.specialtiesAndServices.serviceType}
              </div>
              <RadioButtonsGroup
                name="serviceType"
                labelId="serviceType"
                isLabel={false}
                list={[
                  {
                    id: "single_service",
                    label: branches.addBranch.specialtiesAndServices.singleService,
                    value: "single_service",
                  },
                  {
                    id: "multi_session",
                    label: branches.addBranch.specialtiesAndServices.multiSessions,
                    value: "multi_session",
                  },
                ]}
                value={values["serviceType"] || "single_service"}
                onChange={(e) => {
                  setFieldTouched("serviceType");
                  setFieldValue("serviceType", e.target.value);
                }}
                wrapperClass="flex-row gap-4"
              />
            </div>
          )}
          <Input
            id="serviceNameEN"
            name="serviceNameEN"
            type="text"
            required
            label={
              allowMultiSessions
                ? branches.addBranch.specialtiesAndServices
                    .serviceNameSessionsENTitle
                : branches.addBranch.specialtiesAndServices.serviceNameENTitle
            }
            placeholder={
              allowMultiSessions
                ? branches.addBranch.specialtiesAndServices
                    .serviceNameSessionsENPlaceholder
                : branches.addBranch.specialtiesAndServices
                    .serviceNameENPlaceholder
            }
            value={values["serviceNameEN"]}
            onChange={(value) => {
              setFieldTouched("serviceNameEN");
              setFieldValue("serviceNameEN", value);
            }}
            inputWrapperClass="mb-4"
            labelClassName="text-dark font-medium mb-2"
            isInputHasErr={
              !!(touched["serviceNameEN"] && errors["serviceNameEN"])
            }
            errMsg={errors["serviceNameEN"]}
          />
          <Input
            id="serviceNameAR"
            name="serviceNameAR"
            type="text"
            required
            label={
              allowMultiSessions
                ? branches.addBranch.specialtiesAndServices
                    .serviceNameSessionsARTitle
                : branches.addBranch.specialtiesAndServices.serviceNameARTitle
            }
            placeholder={
              allowMultiSessions
                ? branches.addBranch.specialtiesAndServices
                    .serviceNameSessionsARPlaceholder
                : branches.addBranch.specialtiesAndServices
                    .serviceNameARPlaceholder
            }
            value={values["serviceNameAR"]}
            onChange={(value) => {
              setFieldTouched("serviceNameAR");
              setFieldValue("serviceNameAR", value);
            }}
            inputWrapperClass="mb-4"
            labelClassName="text-dark font-medium mb-2"
            isInputHasErr={
              !!(touched["serviceNameAR"] && errors["serviceNameAR"])
            }
            errMsg={errors["serviceNameAR"]}
          />
        </form>
      }
      isContentText={false}
      maxWidth="sm"
      hasActions={true}
      confirmBtnTxt={shared.save}
      cancelBtnTxt={shared.cancel}
      actionsClass=" px-4"
      confirmBtnClass="btn-secondary fsize-14 text-white py-2 px-5"
      cancelBtnClass="fsize-14 py-2 text-secondary px-5 "
      disableActionBtn={!isValid || !dirty || isLoading}
      handleConfirm={() => {
        handleSubmit();
      }}
      handleCancel={handleCancelOrClose}
    />
  );
};

export default AddEditManualServiceModal;
